import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Store, Megaphone, FileText, Calendar, Tag } from "lucide-react";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

const EMPTY = { associates: [], campaigns: [], documents: [], events: [], promotions: [] };

export function GlobalSearchTrigger({ compact = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const runSearch = useCallback(async (query) => {
    if (!query || query.length < 2) { setResults(EMPTY); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q: query } });
      setResults(data);
    } catch {
      setResults(EMPTY);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => runSearch(q), 220);
    return () => clearTimeout(id);
  }, [q, runSearch]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (path) => { setOpen(false); nav(path); };
  const total = (results.associates.length + results.campaigns.length + results.documents.length + results.events.length + results.promotions.length);

  return (
    <>
      <Button
        variant="outline"
        size={compact ? "icon" : "sm"}
        onClick={() => setOpen(true)}
        className={compact ? "rounded-full" : "rounded-full gap-2 pl-3 pr-4 text-muted-foreground"}
        data-testid="global-search-trigger"
        aria-label={t("common.search")}
      >
        <Search className="w-4 h-4" aria-hidden="true" />
        {!compact && (
          <>
            <span className="text-xs">{t("common.search")}</span>
            <kbd className="ml-2 text-[10px] font-mono bg-secondary rounded px-1.5 py-0.5">⌘K</kbd>
          </>
        )}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          <VisuallyHidden.Root>
            <DialogTitle>{t("common.search")}</DialogTitle>
            <DialogDescription>Cerca a associats, campanyes, documents, activitats i promocions</DialogDescription>
          </VisuallyHidden.Root>
          <div className="border-b border-border p-4">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("common.search") + "…"}
                className="pl-9 rounded-full h-11 border-2"
                data-testid="global-search-input"
                aria-label={t("common.search")}
              />
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto p-3">
            {loading && <div className="text-xs text-muted-foreground p-3">…</div>}
            {!loading && q.length >= 2 && total === 0 && (
              <div className="text-sm text-muted-foreground text-center py-8">Cap resultat per "{q}".</div>
            )}
            {q.length < 2 && (
              <div className="text-xs text-muted-foreground p-3">Escriu almenys 2 caràcters. Cerca a associats, campanyes, documents, activitats i promocions.</div>
            )}

            {results.associates.length > 0 && (
              <Section title="Associats" icon={Store}>
                {results.associates.map(a => (
                  <ResultRow key={a.id} onClick={() => go(a.member_number ? `/comerc/${a.member_number}` : "/directori")} data-testid={`search-associate-${a.id}`}>
                    <div className="font-medium">{a.commercial_name}</div>
                    <div className="text-xs text-muted-foreground">{a.category || "—"} · {a.member_number}</div>
                  </ResultRow>
                ))}
              </Section>
            )}
            {results.campaigns.length > 0 && (
              <Section title="Campanyes" icon={Megaphone}>
                {results.campaigns.map(c => (
                  <ResultRow key={c.id} onClick={() => go("/")} data-testid={`search-campaign-${c.id}`}>
                    <div className="font-medium">{c.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{c.description}</div>
                  </ResultRow>
                ))}
              </Section>
            )}
            {results.documents.length > 0 && (
              <Section title="Documents" icon={FileText}>
                {results.documents.map(d => (
                  <ResultRow key={d.id} onClick={() => go("/associat/serveis")} data-testid={`search-document-${d.id}`}>
                    <div className="font-medium">{d.name}</div>
                    <div className="text-xs text-muted-foreground">{d.folder} · {d.file_type}</div>
                  </ResultRow>
                ))}
              </Section>
            )}
            {results.events.length > 0 && (
              <Section title="Activitats" icon={Calendar}>
                {results.events.map(e => (
                  <ResultRow key={e.id} onClick={() => go("/associat/agenda")} data-testid={`search-event-${e.id}`}>
                    <div className="font-medium">{e.title}</div>
                    <div className="text-xs text-muted-foreground">{new Date(e.start_date).toLocaleDateString()} · {e.location || "—"}</div>
                  </ResultRow>
                ))}
              </Section>
            )}
            {results.promotions.length > 0 && (
              <Section title="Promocions" icon={Tag}>
                {results.promotions.map(p => (
                  <ResultRow key={p.id} onClick={() => go("/associat/avantatges")} data-testid={`search-promotion-${p.id}`}>
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-muted-foreground line-clamp-1">{p.description}</div>
                  </ResultRow>
                ))}
              </Section>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
        <Icon className="w-3 h-3" aria-hidden="true" /> {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ResultRow({ children, onClick, ...rest }) {
  return (
    <button
      className="w-full text-left px-3 py-2 rounded-lg hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  );
}
